import { Box } from "@chakra-ui/react";
import { IRange } from "@the-volte/svc-core-sdk/lib/helpers";
import { add, isAfter, isBefore, isSameDay } from "date-fns";
import React, { useMemo, useState } from "react";
import { ReactSVG } from "react-svg";

import { Button, Icon } from "@components/atoms";
import { useFirstAvailableDate } from "@hooks/useFirstAvailableDate";

import { Overlay } from "../Overlay";
import DatePicker from "./DatePicker/DatePicker";
import * as S from "./styles";

import xImg from "images/x-black.svg";

interface IInputDatePickerProps {
  disabledDays: Date[];
  daysRange: number;
  onSubmit: (range: IRange) => void;
  disabled?: boolean;
  defaultText?: string;
  sleepTime?: number;
  dateFilterStartDate?: Date;
  isPurchase?: boolean;
  preparationDays: number;
  maximumShippingDays: number;
  minimumShippingDays: number;
  samedayPickupEnabled: boolean;
  canPickupToday?: boolean;
}

export const InputDatePicker: React.FC<IInputDatePickerProps> = ({
  disabledDays = [],
  daysRange = 3,
  onSubmit,
  disabled,
  defaultText = "Date required",
  sleepTime = 0,
  dateFilterStartDate,
  isPurchase,
  preparationDays,
  maximumShippingDays,
  minimumShippingDays,
  samedayPickupEnabled = false,
  canPickupToday = false,
}) => {
  const { startDate, endDate, validRange } = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const validRange = daysRange - 1 > 0 ? daysRange - 1 : 0;
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const startDate = dateFilterStartDate || new Date();
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const endDate = add(startDate, { days: validRange });

    return { startDate, endDate, validRange };
  }, [daysRange, dateFilterStartDate]);

  const {
    firstAvailableDate,
    firstAvailablePurchaseDateEnd,
    isDisabledPurchase,
  } = useFirstAvailableDate({
    disabledDays,
    preparationDays,
    maximumShippingDays,
    minimumShippingDays,
    validRange,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [range, setRange] = useState({});

  const dateFilterRange = {
    from: startDate,
    to: endDate,
  };

  const disabledDaysSortedAsc = disabledDays.sort(
    (objA, objB) => Number(objA) - Number(objB)
  );

  let latestDate = disabledDaysSortedAsc[disabledDaysSortedAsc.length - 2];
  const before = disabledDaysSortedAsc[disabledDaysSortedAsc.length - 1] as any;

  latestDate = add(latestDate, { days: preparationDays });
  latestDate.setHours(12, 0, 0, 0);

  const purchase = add(latestDate, { days: validRange });
  purchase.setHours(12, 0, 0, 0);

  let today = add(before.before, { days: 1 });
  today = add(today, { days: preparationDays as number });
  today.setHours(12, 0, 0, 0);

  const isLatestPastToday = latestDate.getTime() > today.getTime();

  const purchaseRange = {
    from: firstAvailableDate,
    to: firstAvailablePurchaseDateEnd,
  };

  const handleOnDayClicked = (date: Date) => {
    const selectedRange = {
      from: date,
      to: add(date, { days: validRange }),
    };
    if (
      !disabledDays.find(
        (d) =>
          isSameDay(d, selectedRange.to) ||
          isSameDay(d, selectedRange.from) ||
          (isAfter(d, selectedRange.from) && isBefore(d, selectedRange.to)) ||
          (d?.before && isAfter(d.before, selectedRange.from))
      )
    ) {
      setRange(selectedRange);
      setTimeout(() => {
        onSubmit(selectedRange);
        setIsOpen(false);
      }, sleepTime);
    }
  };

  const handleSkipDatePicker = () => {
    if (
      !disabledDays.find(
        (d) =>
          isSameDay(d, dateFilterRange.to) ||
          isSameDay(d, dateFilterRange.from) ||
          (isAfter(d, dateFilterRange.from) &&
            isBefore(d, dateFilterRange.to)) ||
          (d?.before && isAfter(d.before, dateFilterRange.from))
      )
    ) {
      setRange(dateFilterRange);
      setTimeout(() => {
        onSubmit(dateFilterRange);
        setIsOpen(false);
      }, sleepTime);
    }
  };

  const handleSkipDatePickerPurchase = () => {
    if (
      !disabledDays.find(
        (d) =>
          isSameDay(d, purchaseRange.to) ||
          isSameDay(d, purchaseRange.from) ||
          (isAfter(d, purchaseRange.from) && isBefore(d, purchaseRange.to))
      )
    ) {
      setRange(purchaseRange);
      setTimeout(() => {
        onSubmit(purchaseRange);
        setIsOpen(false);
      }, sleepTime);
    }
  };

  return (
    <Box h="100%">
      {isPurchase && isLatestPastToday && (
        <S.PurchaseDeliveryDate>
          <Icon name="tick_circle" size={32} viewboxX={16} viewboxY={16} />
          {`This item is currently booked until ${firstAvailableDate.toLocaleDateString(
            "en-GB"
          )}. Your purchase will be checked and delivered after that booking`}
        </S.PurchaseDeliveryDate>
      )}
      {!dateFilterStartDate && !isPurchase && (
        <Button
          testingContext="choose-dates-button"
          disabled={disabled}
          onClick={() => (disabled ? false : setIsOpen(true))}
          bgColor="transparent"
          sx={{
            "&:hover": disabled
              ? {
                  opacity: "0.4",
                  background: "#7182FF".concat(" !important"),
                }
              : undefined,
          }}
        >
          {defaultText}
        </Button>
      )}
      {dateFilterStartDate && !isPurchase && (
        <Button
          testingContext="choose-dates-button"
          disabled={isDisabledPurchase}
          onClick={() => handleSkipDatePicker()}
        >
          {defaultText}
          {/* Skip Date Picker Date Filter */}
        </Button>
      )}

      {isPurchase && (
        <Button
          testingContext="choose-dates-button"
          disabled={disabled}
          onClick={() => handleSkipDatePickerPurchase()}
        >
          {defaultText}
          {/* Skip Date Picker Purchase */}
        </Button>
      )}

      <Overlay
        show={isOpen}
        hide={() => {
          setIsOpen(false);
        }}
      >
        <Box m="auto" textAlign="center">
          <Box
            display="flex"
            justifyContent="flex-end"
            cursor="pointer"
            onClick={() => setIsOpen(false)}
            pt="1rem"
          >
            <ReactSVG src={xImg.src} />
          </Box>
          <Box mt="1rem" maxWidth="20rem" mx="auto">
            {samedayPickupEnabled
              ? "Pick a delivery or pickup date."
              : "Pick a delivery date 1-2 days before your event."}
          </Box>
          <DatePicker
            firstAvailableDate={firstAvailableDate}
            disabledDays={disabledDays}
            range={range}
            handleOnDayClicked={handleOnDayClicked}
            isSamedayEligible={samedayPickupEnabled}
            canPickupToday={canPickupToday}
          />
        </Box>
      </Overlay>
    </Box>
  );
};
InputDatePicker.displayName = "InputDatePicker";
